import { coreQueryKey } from "@/lib/core-query";

export type OperationalContext = {
  id: string;
  tenant_id: string;
  customer_id: string;
  customer_name: string;
  business_unit_id: string;
  business_unit_code: string;
  business_unit_name: string;
  status: string;
};

export type ContextUnitBrief = {
  id: string;
  code: string;
  name: string;
};

export type ContextTenantBrief = {
  id: string;
  display_name: string;
};

export type ContextCustomerGroup = {
  tenant_id: string;
  customer_id: string;
  customer_name: string;
  links: OperationalContext[];
};

export type WorkspaceContext = {
  id: string;
  name: string;
  owner_kind: "tenant" | "business_unit";
  owner_id: string;
  status: string;
  version: number;
};

export type ContextsDocument = {
  kind: "collaborator" | "customer" | "platform";
  unit: ContextUnitBrief | null;
  customers: ContextCustomerGroup[];
  tenants: ContextTenantBrief[];
  items: OperationalContext[];
  workspaces: WorkspaceContext[];
};

export type ContextUnitGroup = {
  business_unit_id: string;
  business_unit_code: string;
  business_unit_name: string;
  items: OperationalContext[];
};

export class ContextsRequestError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export function contextsQueryKey(
  userId: string | undefined | null,
  unitId?: string | null,
  selection?: string,
) {
  return coreQueryKey(userId, "contexts", unitId ?? "", selection ?? "");
}

export function parseClientesParam(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function serializeClientesParam(ids: string[]): string {
  return [...new Set(ids)].join(",");
}

/** Preserve the current operational selection when navigating between local portal routes. */
export function preserveOperationalContextHref(href: string, currentSearch: string): string {
  const target = new URL(href, "http://asv.local");
  if (target.origin !== "http://asv.local") return href;

  const current = new URLSearchParams(currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch);
  for (const key of ["clientes", "workspace_id"]) {
    if (!target.searchParams.has(key) && current.has(key)) {
      target.searchParams.set(key, current.get(key) ?? "");
    }
  }
  return `${target.pathname}${target.search}${target.hash}`;
}

let boundClientes: string[] | null = null;
let boundWorkspace: string | null = null;

export function bindClientesSelection(ids: string[] | null) {
  boundClientes = ids;
}

export function bindWorkspaceSelection(id: string | null) {
  boundWorkspace = id;
}

export function applyClientesParam(path: string): string {
  const url = new URL(path, "http://asv.local");
  if (boundClientes && boundClientes.length > 0 && !url.searchParams.has("clientes")) {
    url.searchParams.set("clientes", serializeClientesParam(boundClientes));
  }
  if (boundWorkspace && !url.searchParams.has("workspace_id")) {
    url.searchParams.set("workspace_id", boundWorkspace);
  }
  return url.pathname + url.search;
}

export function isUsableContext(item: OperationalContext): boolean {
  return item.status === "active";
}

const DIACRITICS_REGEX = /[̀-ͯ]/g;

/** Lowercases and strips diacritics so "sao" matches "São Paulo". */
export function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(DIACRITICS_REGEX, "")
    .toLowerCase()
    .trim();
}

export function contextMatchesQuery(item: OperationalContext, query: string): boolean {
  const needle = normalizeForSearch(query);
  if (!needle) {
    return true;
  }
  const haystack = normalizeForSearch(
    `${item.customer_name} ${item.business_unit_name} ${item.business_unit_code}`,
  );
  return haystack.includes(needle);
}

export function groupContextsByUnit(items: OperationalContext[]): ContextUnitGroup[] {
  const groups = new Map<string, ContextUnitGroup>();
  for (const item of items) {
    if (!isUsableContext(item)) {
      continue;
    }
    let group = groups.get(item.business_unit_id);
    if (!group) {
      group = {
        business_unit_id: item.business_unit_id,
        business_unit_code: item.business_unit_code,
        business_unit_name: item.business_unit_name,
        items: [],
      };
      groups.set(item.business_unit_id, group);
    }
    group.items.push(item);
  }
  return [...groups.values()].sort((a, b) => {
    const byCode = a.business_unit_code.localeCompare(b.business_unit_code, "pt-BR");
    if (byCode !== 0) {
      return byCode;
    }
    return a.business_unit_name.localeCompare(b.business_unit_name, "pt-BR");
  });
}

export async function fetchOperationalContexts(signal?: AbortSignal, workspaceId?: string | null): Promise<ContextsDocument> {
  const path = new URL("/api/v1/me/contexts", "http://asv.local");
  if (workspaceId) path.searchParams.set("workspace_id", workspaceId);
  const res = await fetch(path.pathname + path.search, {
    credentials: "include",
    cache: "no-store",
    headers: { accept: "application/json" },
    signal,
  });
  if (!res.ok) {
    throw new ContextsRequestError("Não foi possível carregar os contextos", res.status);
  }
  const body = (await res.json()) as Partial<ContextsDocument> & { items?: OperationalContext[] };
  const items = (body.items ?? []).filter(isUsableContext);
  return {
    kind: body.kind === "collaborator" || body.kind === "customer" || body.kind === "platform" ? body.kind : "platform",
    unit: body.unit ?? null,
    customers: body.customers ?? [],
    tenants: body.tenants ?? [],
    items,
    workspaces: body.workspaces ?? [],
  };
}
